import IconUser16 from '@warp-ds/icons/react/user-16';
import cn from 'classnames';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SimpleHead } from 'shared/page/Head';
import { Main } from 'shared/page/Main';
import { SortDetails } from 'shared/page/SortDetails';
import { Dropdown } from 'shared/search/common/Dropdown';
import { FrontPageModal } from 'shared/search/common/frontPageModal/FrontPageModal';
import { PaxCombination } from 'shared/search/prepackage/Pax/PaxCombination';
import { formatPaxTriggerLabel } from 'shared/search/prepackage/Pax/PaxUtils';
import { useUnleash } from '@/hooks/unleash';
import type { PaleState, ThunkDispatchFunc } from '@/interfaces';
import { actions } from '@/state/filterActions';
import { fetchResults } from '@/state/resultActions';
import { Breadcrumbs } from './breadcrumbs/Breadcrumbs';
import { DestinationFilter } from './filters/checkbox/DestinationFilter';
import CurrentFilters from './filters/currentFilters/CurrentFilters';
import { FilterGroup } from './filters/FilterGroup';
import { Filters } from './filters/Filters';
import ResultOriginDropdown from './filters/origin/ResultOriginDropdown';
import ResultTravelTimePicker from './filters/travelTime/ResultTravelTimePicker';
import { TopBar } from './resultList/components/topBar/TopBar';
import { ResultList } from './resultList/ResultList';
import DesktopTopBar from './topBar/DesktopTopBar';
import MobileTopBar from './topBar/MobileTopBar';

export const ResultPageLayout = () => {
    const dispatch = useDispatch<ThunkDispatchFunc>();
    const unleash = useUnleash();

    const filtersOpen = useSelector((s: PaleState) => s.appState.filtersOpen);
    const numberOfAdults = useSelector((s: PaleState) => s.filters.numberOfAdults);
    const agesOfChildren = useSelector((s: PaleState) => s.filters.agesOfChildren);

    const updatePax = useCallback(
        (adults: number, ages: number[]) => {
            dispatch(actions.changePaxCombination({ numberOfAdults: adults, agesOfChildren: ages }));
            dispatch(fetchResults());
        },
        [dispatch],
    );

    return (
        <>
            <SimpleHead
                title="Pakkereiser - sammenlign pris | FINN reise"
                canonical="https://www.finn.no/reise/pakkereiser/resultat"
                description="Finn billige pakkereiser fra alle reisebyrå på ett sted! På FINN.no kan du sammenligne priser og spare penger."
            />
            <Main broadcastUrl="/broadcasts?path=www.finn.no/reise/pakkereiser">
                {!unleash?.topBarFilterUpdate && <MobileTopBar />}
                <h1 className="sr-only">Pakkereiser</h1>
                {!unleash?.topBarFilterUpdate && <DesktopTopBar />}
                <div className={cn('md:grid md:grid-cols-3 md:gap-32 pb-32', { 'pt-16': unleash?.topBarFilterUpdate })}>
                    <div className={cn('divide-y-1', { 'hidden md:block': !filtersOpen })}>
                        <div test-id="origin" className={cn('px-8 py-16', { 'mt-8': unleash?.topBarFilterUpdate })}>
                            {unleash?.topBarFilterUpdate && <Breadcrumbs vertical="pp" className="hidden md:inline" />}
                            <h3 className={cn('h4', { 'my-16': unleash?.topBarFilterUpdate, 'mb-16': !unleash?.topBarFilterUpdate })}>
                                Utreise og dato
                            </h3>
                            <ResultOriginDropdown />
                            <h3 className="sr-only">Alternativer for avreise og hjemreise</h3>
                            <ResultTravelTimePicker />
                            <div className="mt-16">
                                <Dropdown
                                    trigger={{
                                        text: formatPaxTriggerLabel(numberOfAdults, agesOfChildren),
                                        label: 'Reisende',
                                    }}
                                    prefix={<IconUser16 aria-hidden />}
                                    className="hidden sm:flex">
                                    <PaxCombination numberOfAdults={numberOfAdults} agesOfChildren={agesOfChildren} updatePax={updatePax} />
                                </Dropdown>
                                <FrontPageModal
                                    breakpoint="sm"
                                    title="Reisende"
                                    trigger={{
                                        text: formatPaxTriggerLabel(numberOfAdults, agesOfChildren),
                                        prefix: <IconUser16 aria-hidden />,
                                    }}>
                                    <PaxCombination numberOfAdults={numberOfAdults} agesOfChildren={agesOfChildren} updatePax={updatePax} />
                                </FrontPageModal>
                            </div>
                        </div>
                        <FilterGroup title="Reisemål" testId="destination">
                            <DestinationFilter />
                        </FilterGroup>
                        <Filters />
                    </div>
                    {unleash?.topBarFilterUpdate ? (
                        <div className="md:col-span-2">
                            <TopBar />
                            <div className={cn('md:col-span-2 ', { 'hidden md:block': filtersOpen })}>
                                <SortDetails section="pakkereiser" className="md:hidden pt-16 mt-16" />
                                <CurrentFilters />
                                <ResultList unleash={unleash} />
                            </div>
                        </div>
                    ) : (
                        <section className={cn('md:col-span-2', { 'hidden md:block': filtersOpen })}>
                            <SortDetails section="pakkereiser" className="md:hidden py-16" />
                            <CurrentFilters />
                            <ResultList unleash={unleash} />
                        </section>
                    )}
                </div>
            </Main>
        </>
    );
};
